import type { SkillBranchId, SkillNodeDef, SkillNodeEffects, SkillPathId } from "./schemas";

/** Roots so filler nodes chain off the live tree instead of all unlocking at once. */
const PATH_ROOT: Partial<Record<SkillPathId, string>> = {
  bulwark: "reinforced_plating",
  glass: "glass_hull",
  nap: "nap_trim",
  high: "high_trim",
  scrapper: "salvage_tuned",
};

const RIVAL: Partial<Record<SkillPathId, string>> = {
  bulwark: "glass_hull",
  glass: "reinforced_plating",
  nap: "high_trim",
  high: "nap_trim",
};

const PATH_META: Record<SkillPathId, { branch: SkillBranchId; names: readonly string[] }> = {
  bulwark: {
    branch: "hull",
    names: [
      "Keel Plate",
      "Rib Stitch",
      "Bulkhead Pin",
      "Spall Liner",
      "Brace Collar",
      "Impact Web",
      "Anvil Skin",
      "Forge Rib",
      "Citadel Stud",
      "Ram Keel",
      "Slab Door",
      "Weld Bead",
      "Shock Pad",
      "Castle Ring",
      "Iron Wake",
      "Last Plate",
    ],
  },
  glass: {
    branch: "weapons",
    names: [
      "Thin Skin",
      "Hot Core",
      "Needle Feed",
      "Overbore",
      "Shiver Plate",
      "White Tracer",
      "Bleed Vent",
      "Razorburst",
      "Cinder Tip",
      "Hard Light",
      "Crack Chamber",
      "Fever Rail",
      "Splinter Mag",
      "Sun Wire",
      "Brittle Heart",
      "Last Spark",
    ],
  },
  nap: {
    branch: "mobility",
    names: [
      "Dust Trim",
      "Skim Vane",
      "Grit Keel",
      "Close Hook",
      "Sand Bite",
      "Pressure Jet",
      "Canyon Cut",
      "Ash Skirt",
      "Rumble Trim",
      "Assault Spike",
      "Pressure Burn",
      "Dune Nail",
      "Scrap Wake",
      "Breach Fan",
      "Dirt Song",
      "Final Pressure",
    ],
  },
  high: {
    branch: "mobility",
    names: [
      "Velocity Trim",
      "Hot Vector",
      "Vector Hook",
      "Drive Keel",
      "Fast Line",
      "Overdrive",
      "Surge Jet",
      "Apex Fan",
      "Burn Lane",
      "Vector Nail",
      "Limit Cut",
      "Reactor Bite",
      "Drive Wire",
      "Fast Wake",
      "Ion Trim",
      "Final Vector",
    ],
  },
  scrapper: {
    branch: "utility",
    names: [
      "Magnet Lip",
      "Scoop Arm",
      "Tag Net",
      "Loot Latch",
      "Sweep Comb",
      "Claim Hook",
      "Spare Bin",
      "Yank Coil",
      "Pad Leech",
      "Core Tack",
      "Salvage Ear",
      "Haul Belt",
      "Scrap Song",
      "Courier Pin",
      "Yard Key",
      "Last Claim",
    ],
  },
  aegis: {
    branch: "shield",
    names: [
      "Ward Skin",
      "Halo Plate",
      "Screen Rib",
      "Aegis Pin",
      "Bubble Stitch",
      "Guard Fan",
      "Bastion Film",
      "Cover Arc",
      "Mirror Rim",
      "Hold Field",
      "Shell Hymn",
      "Rampart Veil",
      "Safe Wake",
      "Dome Nail",
      "Ward Lock",
      "Last Ward",
    ],
  },
  kinetic: {
    branch: "weapons",
    names: [
      "Slug Feed",
      "Mass Driver",
      "Hard Round",
      "Anvil Shot",
      "Recoil Brace",
      "Pellet Storm",
      "Ram Mag",
      "Punch Cap",
      "Steel Rain",
      "Impact Tune",
      "Breach Slug",
      "Heavy Chamber",
      "Kinetic Bite",
      "Forge Round",
      "Pile Driver",
      "Last Slug",
    ],
  },
  arc: {
    branch: "weapons",
    names: [
      "Arc Coil",
      "Ion Lash",
      "Spark Rail",
      "Phase Tip",
      "Volt Fan",
      "Blue Needle",
      "Surge Cap",
      "Plasma Lip",
      "Charge Hymn",
      "Fork Bolt",
      "Ion Wake",
      "Grid Burn",
      "Lash Wire",
      "Storm Mag",
      "Pulse Nail",
      "Last Arc",
    ],
  },
  momentum: {
    branch: "mobility",
    names: [
      "Afterburn",
      "Slide Trim",
      "Boost Well",
      "Vector Bite",
      "Drift Pin",
      "Dash Coil",
      "Inertia Lip",
      "Rush Fan",
      "Throttle Hymn",
      "Line Cut",
      "Surge Wake",
      "Sprint Nail",
      "Burn Lane",
      "Vector Lock",
      "Goad Jet",
      "Last Burn",
    ],
  },
  ghost: {
    branch: "utility",
    names: [
      "Soft Return",
      "Fade Trim",
      "Quiet Hull",
      "Slip Film",
      "Ghost Pin",
      "Blind Spot",
      "Hush Vane",
      "Shadow Lip",
      "Skip Wake",
      "Null Paint",
      "Whisper Fan",
      "Sidestep",
      "Pale Wake",
      "Lost Signal",
      "Phase Skirt",
      "Last Ghost",
    ],
  },
};

function effectFor(path: SkillPathId, index: number): SkillNodeEffects {
  const t = (index % 4) + 1;
  switch (path) {
    case "bulwark":
      return t % 2 === 0 ? { hullBonus: 8 + t } : { armorBonus: 3 + t };
    case "glass":
      return { damageMult: 1.04 + t * 0.01, hullBonus: -4 };
    case "nap":
    case "high":
      return t % 2 === 0 ? { speedMult: 1.04 } : { damageMult: 1.03 };
    case "scrapper":
      return t % 2 === 0 ? { salvageMult: 1.05 } : { extractBonus: 0.06 };
    case "aegis":
      return t % 2 === 0 ? { shieldBonus: 10 + t * 2 } : { shieldMult: 1.04 };
    case "kinetic":
      return { damageMult: 1.05 + (t === 4 ? 0.02 : 0) };
    case "arc":
      return t % 2 === 0 ? { damageMult: 1.04 } : { cooldownMult: 0.96 };
    case "momentum":
      return t % 2 === 0 ? { speedMult: 1.05 } : { boostBonus: 0.08 };
    case "ghost":
      return t % 2 === 0 ? { speedMult: 1.03 } : { salvageMult: 1.03 };
  }
}

export const LATE_UNLOCK_PATHS: ReadonlySet<SkillPathId> = new Set([
  "aegis",
  "kinetic",
  "arc",
  "momentum",
  "ghost",
]);

function describeEffects(fx: SkillNodeEffects, path: SkillPathId): string {
  const bits: string[] = [];
  if (fx.hullBonus) bits.push(`${fx.hullBonus > 0 ? "+" : ""}${fx.hullBonus} hull`);
  if (fx.armorBonus) bits.push(`+${fx.armorBonus} armor`);
  if (fx.shieldBonus) bits.push(`+${fx.shieldBonus} shield`);
  if (fx.damageMult) bits.push(`×${fx.damageMult.toFixed(2)} damage`);
  if (fx.speedMult) bits.push(`×${fx.speedMult.toFixed(2)} speed`);
  if (fx.salvageMult) bits.push(`×${fx.salvageMult.toFixed(2)} salvage`);
  if (fx.cooldownMult) bits.push(`×${fx.cooldownMult.toFixed(2)} cooldown`);
  if (fx.boostBonus) bits.push(`+${Math.round(fx.boostBonus * 100)}% boost`);
  if (fx.extractBonus) bits.push(`+${Math.round(fx.extractBonus * 100)}% extract`);
  if (fx.shieldMult) bits.push(`×${fx.shieldMult.toFixed(2)} shield`);
  return bits.length > 0 ? bits.join(", ") : `${path} doctrine`;
}

const SEEDED_PATH: Record<string, SkillPathId> = {
  reinforced_plating: "bulwark",
  ablative_armor: "bulwark",
  hull_weave_2: "bulwark",
  emergency_seal: "bulwark",
  composite_plating: "bulwark",
  ram_brace: "bulwark",
  keystone_bulwark: "bulwark",
  hard_shell_skill: "bulwark",
  barrier_dense: "bulwark",
  glass_hull: "glass",
  hot_rounds: "glass",
  breach_tuned_skill: "glass",
  glass_core: "glass",
  keystone_glass: "glass",
  overcharge: "glass",
  nap_trim: "nap",
  deep_nap: "nap",
  nap_hunter: "nap",
  dust_keel: "nap",
  high_trim: "high",
  high_rider: "high",
  sky_keel: "high",
  thin_air: "high",
  salvage_tuned: "scrapper",
  magnetic_sweep: "scrapper",
  loot_courier_skill: "scrapper",
  field_salvager: "scrapper",
  keystone_scavenger: "scrapper",
  quick_extract: "scrapper",
  beacon_boost: "scrapper",
  magnet_well: "scrapper",
  pad_thief: "scrapper",
};

function knownPath(node: SkillNodeDef): SkillPathId | undefined {
  return node.path ?? SEEDED_PATH[node.id];
}

/** Append enough authored filler nodes so every lane has ≥16 entries. */
export function fillMissingPathNodes(existing: readonly SkillNodeDef[]): SkillNodeDef[] {
  const have: Record<SkillPathId, number> = {
    bulwark: 0,
    glass: 0,
    nap: 0,
    high: 0,
    scrapper: 0,
    aegis: 0,
    kinetic: 0,
    arc: 0,
    momentum: 0,
    ghost: 0,
  };
  for (const node of existing) {
    const path = knownPath(node);
    if (path) have[path] += 1;
  }

  const extra: SkillNodeDef[] = [];
  (Object.keys(PATH_META) as SkillPathId[]).forEach((path) => {
    const meta = PATH_META[path];
    const start = have[path];
    for (let i = start; i < 16; i += 1) {
      const id = `${path}_lane_${i}`;
      const prev =
        i === 0 ? PATH_ROOT[path] : i === start ? PATH_ROOT[path] : `${path}_lane_${i - 1}`;
      const rival = RIVAL[path];
      const effects = effectFor(path, i);
      extra.push({
        id,
        name: meta.names[i] ?? `${path} ${i + 1}`,
        description: describeEffects(effects, path),
        branch: meta.branch,
        tier: Math.min(3, Math.floor(i / 6) + 1) as 1 | 2 | 3,
        path,
        prerequisites: prev ? [prev] : [],
        incompatibleWith: rival ? [rival] : [],
        maxStacks: 1,
        effects,
      });
    }
  });
  return extra;
}
